import React from 'react';
import { Typography, Button, Divider } from 'antd';
import { useSelector } from 'react-redux';
import {
    boxStyles,
    buttonStyles,
    typography
} from '../../theme/theme';

const { Title, Text } = Typography;

const ColorSystem = () => {
    // Prendiamo i colori dallo store Redux
    const theme = useSelector(state => state.theme);

    // Colori di default
    const PRIMARY = theme.variant.primary;
    const ACCENT = theme.variant.accent;
    const BLACK = theme.variant.black;
    const SECONDARY = theme.variant.secondary;
    const COMPLEMENTARY = theme.variant.complementary;
    const WHITE = theme.variant.white;

    // Colori di stato
    const SUCCESS = theme.status.success;
    const ERROR = theme.status.error;
    const WARNING = theme.status.warning;
    const INFO = theme.status.info;

    // Sfumature primarie
    const PRIMARY_100 = theme.primary.primary100;
    const PRIMARY_200 = theme.primary.primary200;
    const PRIMARY_300 = theme.primary.primary300;
    const PRIMARY_400 = theme.primary.primary400;
    const PRIMARY_500 = theme.primary.primary500;
    const PRIMARY_600 = theme.primary.primary600;
    const PRIMARY_700 = theme.primary.primary700;

    // Sfumature secondarie
    const SECONDARY_100 = theme.secondary.secondary100;
    const SECONDARY_200 = theme.secondary.secondary200;
    const SECONDARY_300 = theme.secondary.secondary300;
    const SECONDARY_400 = theme.secondary.secondary400;
    const SECONDARY_500 = theme.secondary.secondary500;
    const SECONDARY_600 = theme.secondary.secondary600;
    const SECONDARY_700 = theme.secondary.secondary700;

    // Sfumature complementari
    const COMPLEMENTARY_100 = theme.complementary.complementary100;
    const COMPLEMENTARY_200 = theme.complementary.complementary200;
    const COMPLEMENTARY_300 = theme.complementary.complementary300;
    const COMPLEMENTARY_400 = theme.complementary.complementary400;
    const COMPLEMENTARY_500 = theme.complementary.complementary500;
    const COMPLEMENTARY_600 = theme.complementary.complementary600;
    const COMPLEMENTARY_700 = theme.complementary.complementary700;

    // Sfumature di grigio
    const GREY_100 = theme.grey.grey100;
    const GREY_200 = theme.grey.grey200;
    const GREY_300 = theme.grey.grey300;
    const GREY_400 = theme.grey.grey400;
    const GREY_500 = theme.grey.grey500;
    const GREY_600 = theme.grey.grey600;
    const GREY_700 = theme.grey.grey700;

    // Colori di sfondo
    const BG_PRIMARY = theme.background.primaryBg;
    const BG_WHITE = theme.background.whiteBg;
    const BG_GREY = theme.background.greyBg;
    const BG_SECONDARY = theme.background.secondaryBg;

    const ColorBox = ({ color, label, hexCode }) => (
        <div style={{
            marginBottom: 'clamp(0.625rem, 2vw, 1.25rem)',
            width: 'clamp(5rem, 10vw, 7.5rem)'
        }}>
            <div
                style={{
                    backgroundColor: hexCode,
                    height: 'clamp(1.875rem, 4vw, 2.8125rem)',
                    width: '100%',
                    borderRadius: 'clamp(0.125rem, 0.5vw, 0.25rem)',
                    marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)',
                    border: hexCode === '#FFFFFF' || hexCode.toLowerCase() === '#ffffff' ? 'clamp(0.0625rem, 0.2vw, 0.0625rem) solid #E0E0E0' : 'none'
                }}
            />
            <div style={{ textAlign: 'left' }}>
                <Text strong style={{ display: 'block' }}>{label}</Text>
                <Text type="secondary" style={{ fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)' }}>{hexCode}</Text>
            </div>
        </div>
    );

    const BoxExample = ({ title, style }) => (
        <div
            style={{
                ...style,
                width: '100%',
                height: 'clamp(2.5rem, 5vw, 3.75rem)',
                borderRadius: 'clamp(0.125rem, 0.5vw, 0.25rem)',
                padding: 'clamp(0.5rem, 1.5vw, 1rem)',
                marginBottom: 'clamp(0.25rem, 1vw, 0.5rem)'
            }}
        >
            <Text>{title}</Text>
        </div>
    );

    return (
        <div>
            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Default Colors</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.625rem, 2vw, 1.25rem)' }}>
                <ColorBox color="primary" label="Primary" hexCode={PRIMARY} />
                <ColorBox color="accent" label="Accent" hexCode={ACCENT} />
                <ColorBox color="black" label="Black" hexCode={BLACK} />
                <ColorBox color="secondary" label="Secondary" hexCode={SECONDARY} />
                <ColorBox color="complementary" label="Complementary" hexCode={COMPLEMENTARY} />
                <ColorBox color="white" label="White" hexCode={WHITE} />
            </div>

            <Divider />

            {/* Status Colors */}
            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Status Colors</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.625rem, 2vw, 1.25rem)' }}>
                <ColorBox color="success" label="Success" hexCode={SUCCESS} />
                <ColorBox color="error" label="Error" hexCode={ERROR} />
                <ColorBox color="warning" label="Warning" hexCode={WARNING} />
                <ColorBox color="info" label="Info" hexCode={INFO} />
            </div>

            <Divider />

            {/* Primary Shades */}
            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Primary Shades</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.625rem, 2vw, 1.25rem)' }}>
                <ColorBox color="primary100" label="Primary 100" hexCode={PRIMARY_100} />
                <ColorBox color="primary200" label="Primary 200" hexCode={PRIMARY_200} />
                <ColorBox color="primary300" label="Primary 300" hexCode={PRIMARY_300} />
                <ColorBox color="primary400" label="Primary 400" hexCode={PRIMARY_400} />
                <ColorBox color="primary500" label="Primary 500" hexCode={PRIMARY_500} />
                <ColorBox color="primary600" label="Primary 600" hexCode={PRIMARY_600} />
                <ColorBox color="primary700" label="Primary 700" hexCode={PRIMARY_700} />
            </div>

            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Secondary Shades</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.625rem, 2vw, 1.25rem)' }}>
                <ColorBox color="secondary100" label="Secondary 100" hexCode={SECONDARY_100} />
                <ColorBox color="secondary200" label="Secondary 200" hexCode={SECONDARY_200} />
                <ColorBox color="secondary300" label="Secondary 300" hexCode={SECONDARY_300} />
                <ColorBox color="secondary400" label="Secondary 400" hexCode={SECONDARY_400} />
                <ColorBox color="secondary500" label="Secondary 500" hexCode={SECONDARY_500} />
                <ColorBox color="secondary600" label="Secondary 600" hexCode={SECONDARY_600} />
                <ColorBox color="secondary700" label="Secondary 700" hexCode={SECONDARY_700} />
            </div>

            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Complementary Shades</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.625rem, 2vw, 1.25rem)' }}>
                <ColorBox color="complementary100" label="Complementary 100" hexCode={COMPLEMENTARY_100} />
                <ColorBox color="complementary200" label="Complementary 200" hexCode={COMPLEMENTARY_200} />
                <ColorBox color="complementary300" label="Complementary 300" hexCode={COMPLEMENTARY_300} />
                <ColorBox color="complementary400" label="Complementary 400" hexCode={COMPLEMENTARY_400} />
                <ColorBox color="complementary500" label="Complementary 500" hexCode={COMPLEMENTARY_500} />
                <ColorBox color="complementary600" label="Complementary 600" hexCode={COMPLEMENTARY_600} />
                <ColorBox color="complementary700" label="Complementary 700" hexCode={COMPLEMENTARY_700} />
            </div>

            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Grey Shades</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.625rem, 2vw, 1.25rem)' }}>
                <ColorBox color="grey100" label="Grey 100" hexCode={GREY_100} />
                <ColorBox color="grey200" label="Grey 200" hexCode={GREY_200} />
                <ColorBox color="grey300" label="Grey 300" hexCode={GREY_300} />
                <ColorBox color="grey400" label="Grey 400" hexCode={GREY_400} />
                <ColorBox color="grey500" label="Grey 500" hexCode={GREY_500} />
                <ColorBox color="grey600" label="Grey 600" hexCode={GREY_600} />
                <ColorBox color="grey700" label="Grey 700" hexCode={GREY_700} />
            </div>

            <Divider />

            {/* Background Colors */}
            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Background Colors</Title>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'clamp(0.625rem, 2vw, 1.25rem)' }}>
                <ColorBox color="bgPrimary" label="Primary Background" hexCode={BG_PRIMARY} />
                <ColorBox color="bgWhite" label="White Background" hexCode={BG_WHITE} />
                <ColorBox color="bgGrey" label="Grey Background" hexCode={BG_GREY} />
                <ColorBox color="bgSecondary" label="Secondary Background" hexCode={BG_SECONDARY} />
            </div>

            <Divider />

            {/* Box Styles */}
            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Box - 4px Rounded</Title>
            <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 'clamp(18.75rem, 50vw, 31.25rem)' }}>
                <BoxExample
                    title="Default Box"
                    style={boxStyles.defaultBox}
                />
                <BoxExample
                    title="Border Box"
                    style={boxStyles.borderBox}
                />
                <BoxExample
                    title="Grey Box"
                    style={boxStyles.greyBox}
                />
                <BoxExample
                    title="Raised"
                    style={boxStyles.raisedBox}
                />
                <BoxExample
                    title="Floating"
                    style={boxStyles.floatingBox}
                />
            </div>

            <Divider />

            {/* Button/Link Styles */}
            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Button/Link - 4px Rounded</Title>
            <div>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'clamp(0.3125rem, 1vw, 0.625rem)',
                    border: 'clamp(0.0625rem, 0.2vw, 0.0625rem) dotted #ccc',
                    padding: 'clamp(0.625rem, 1.5vw, 0.9375rem)',
                    borderRadius: 'clamp(0.125rem, 0.5vw, 0.25rem)'
                }}>
                    <Button type="primary" style={buttonStyles.primary}>Primary</Button>
                    <Button style={buttonStyles.outline}>Outline</Button>
                    <Button style={buttonStyles.secondary}>Secondary</Button>
                    <Button style={buttonStyles.accent}>Accent</Button>
                    <Button disabled>Disable</Button>
                </div>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'clamp(0.3125rem, 1vw, 0.625rem)',
                    border: 'clamp(0.0625rem, 0.2vw, 0.0625rem) dotted #ccc',
                    padding: 'clamp(0.625rem, 1.5vw, 0.9375rem)',
                    borderRadius: 'clamp(0.125rem, 0.5vw, 0.25rem)'
                }}>
                    <Button style={buttonStyles.success}>Success</Button>
                    <Button style={buttonStyles.info}>Information</Button>
                    <Button style={buttonStyles.warning}>Warning</Button>
                    <Button style={buttonStyles.error}>Error</Button>
                </div>
            </div>

            <Divider />

            {/* Typography */}
            <Title level={3} style={{ marginBottom: 'clamp(1rem, 2vw, 1.5rem)', color: PRIMARY_700, textAlign: 'left' }}>Typography</Title>
            <div style={{ marginBottom: 'clamp(0.75rem, 1.5vw, 1.25rem)', fontFamily: typography.fontFamily }}>
                <Title level={1} style={{ margin: 'clamp(0.25rem, 1vw, 0.5rem) 0' }}>Inter - H1 - {typography.fontSizes.h1}</Title>
                <Title level={2} style={{ margin: 'clamp(0.25rem, 1vw, 0.5rem) 0' }}>Inter - H2 - {typography.fontSizes.h2}</Title>
                <Title level={3} style={{ margin: 'clamp(0.25rem, 1vw, 0.5rem) 0' }}>Inter - H3 - {typography.fontSizes.h3}</Title>
                <Title level={4} style={{ margin: 'clamp(0.25rem, 1vw, 0.5rem) 0' }}>Inter - H4 - {typography.fontSizes.h4}</Title>
                <Title level={5} style={{ margin: 'clamp(0.25rem, 1vw, 0.5rem) 0' }}>Inter - H5 - {typography.fontSizes.h5}</Title>
                <Text type="secondary">Inter - LABEL - {typography.fontSizes.label}</Text>
            </div>

            <div>
                <Text underline>Underline: Example on white background</Text>
                <div style={{ margin: 'clamp(0.3125rem, 1.2vw, 0.625rem) 0' }}></div>
                <Text>Normal: Example on white background</Text>
            </div>
        </div>
    );
};

export default ColorSystem;